import useHeader from '@hooks/useHeader';
import {
    Autocomplete,
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DevTool } from '@hookform/devtools';
import FieldSearchAutocomplete from '@Components/General/AsyncAutocomplete';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { LoadingButton } from '@mui/lab';
import { getDirtyFields } from '@utils/utils';
import { countriesOptions } from '@utils/globalOptions';
import { useNavigate, useParams } from '@tanstack/react-router';
import { contactQueryOptions } from '@utils/queryOptions';
import ProfilePictureField from '../../../Components/General/profile-picture-field';
import RhfRtlProvider from '@dev/rhf-rtl-provider';
import useFormMui from '@hooks/useFormMui';
import { omit } from 'lodash';

const wrapLastValue = (values, wrapperKey) => {
    if (values.length === 0) return [];
    const lastValue = values[values.length - 1];
    if (typeof lastValue === 'string') {
        return [...values.slice(0, -1), { [wrapperKey]: lastValue }];
    }
    return values;
};

const EditContact = ({}) => {
    const { t } = useTranslation();
    const { contactId } = useParams({});
    const navigate = useNavigate();

    const isNew = contactId === 'new';

    const { data: contact } = useSuspenseQuery(contactQueryOptions(contactId));

    useHeader({
        title: isNew ? t('New Contact') : t('Edit Contact'),
    });

    const getDefaultValue = contact => ({
        prefix: contact?.prefix || '',
        first_name: contact?.first_name || '',
        last_name: contact?.last_name || '',
        suffix: contact?.suffix || '',
        street: contact?.street || '',
        neighborhood: contact?.neighborhood || '',
        city: contact?.city || '',
        state: contact?.state || '',
        country: contact?.country || '',
        phone_numbers: contact?.phone_numbers || [],
        email_addresses: contact?.email_addresses || [],
        house_number: contact?.house_number || '',
        apartment_number: contact?.apartment_number || '',
        zip_code: contact?.zip_code || '',
        address_notes: contact?.address_notes || '',
        description: contact?.description || '',
        profile_picture: contact?.profile_picture || '',
    });

    const {
        mutateAsync: saveContact,
        isPending: saving,
        error,
    } = useMutation({
        mutationKey: [isNew ? 'createContact' : 'updateContact'],
    });

    const {
        register,
        control,
        handleSubmit,
        watch,
        reset,
        formState: { isDirty, dirtyFields },
    } = useFormMui({
        defaultValues: getDefaultValue(contact),
        mode: 'onChange',
        errors: error?.status === 400 && error?.response?.data,
    });

    const fieldsToWatch = [
        'street',
        'neighborhood',
        'city',
        'state',
        'country',
    ];

    const formValues = watch(fieldsToWatch);
    const streetParams = {
        street__icontains: formValues[0] || undefined,
        neighborhood__icontains: formValues[1] || undefined,
        city__icontains: formValues[2] || undefined,
        state__icontains: formValues[3] || undefined,
        country__icontains: formValues[4] || undefined,
    };
    const neighborhoodParams = omit(streetParams, ['street__icontains']);
    const cityParams = omit(neighborhoodParams, ['neighborhood__icontains']);
    const stateParams = omit(cityParams, ['city__icontains']);

    const onSubmit = (data, close = false) => {
        const formattedData = getDirtyFields(data, dirtyFields);
        const postData = isNew ? formattedData : [contactId, formattedData];
        return saveContact(postData).then(res => {
            reset(getDefaultValue(res));
            if (isNew) {
                if (close === true)
                    navigate({
                        to: '../../$contactId',
                        params: { contactId: res.contact_id },
                    });
                else
                    navigate({
                        to: '../../$contactId/edit',
                        params: { contactId: res.contact_id.toString() },
                    });
            } else {
                if (close === true) navigate({ to: '../' });
            }
        });
    };

    return (
        <>
            <Stack spacing={2}>
                {/* Top Bar */}
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography variant="titleLarge">
                        {t('Contact Details')}
                    </Typography>
                    <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <LoadingButton
                            onClick={handleSubmit(onSubmit)}
                            variant="tonal"
                            size="large"
                            disabled={!isDirty}
                            loading={saving}
                        >
                            {t('Save')}
                        </LoadingButton>
                        <LoadingButton
                            onClick={handleSubmit(data => onSubmit(data, true))}
                            variant="filled"
                            size="large"
                            disabled={!isDirty}
                            loading={saving}
                        >
                            {t('Save and Close')}
                        </LoadingButton>
                    </Stack>
                </Box>
                {/* Name and Profile Picture */}
                <Card>
                    <CardContent>
                        <Stack direction="row" spacing={4}>
                            <ProfilePictureField
                                control={control}
                                name="profile_picture"
                                text={contact?.display_name}
                            />
                            <Stack spacing={2} width="100%">
                                <Typography variant="titleLarge">
                                    {t('Contact Information')}
                                </Typography>
                                <Stack direction="row" spacing={2}>
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        size="small"
                                        label={t('First Name')}
                                        {...register('first_name')}
                                    />
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        size="small"
                                        label={t('Last Name')}
                                        {...register('last_name')}
                                    />
                                </Stack>
                                <Stack direction="row" spacing={2}>
                                    <FieldSearchAutocomplete
                                        control={control}
                                        label={t('Prefix')}
                                        name="prefix"
                                    />

                                    <FieldSearchAutocomplete
                                        control={control}
                                        label={t('Suffix')}
                                        name="suffix"
                                    />
                                </Stack>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>
                {/* Communication */}
                <Card>
                    <CardHeader title={t('Communication')} />
                    <CardContent>
                        <Stack direction="row" spacing={4}>
                            <Controller
                                name="phone_numbers"
                                control={control}
                                render={({
                                    field: { onChange, ...rest },
                                    fieldState: { error },
                                }) => (
                                    <Autocomplete
                                        multiple
                                        fullWidth
                                        freeSolo
                                        autoSelect
                                        options={[]}
                                        {...rest}
                                        onChange={(_, newValue) =>
                                            onChange(
                                                wrapLastValue(
                                                    newValue,
                                                    'phone_number',
                                                ),
                                            )
                                        }
                                        getOptionLabel={option =>
                                            option.phone_number
                                        }
                                        ChipProps={{ color: 'primary' }}
                                        renderTags={(
                                            value,
                                            getTagProps,
                                            ownerState,
                                        ) => {
                                            const valuesWithErrors = value.map(
                                                (val, index) => ({
                                                    ...val,
                                                    error: error?.[index]
                                                        ?.phone_number,
                                                }),
                                            );
                                            return valuesWithErrors.map(
                                                (o, i) => (
                                                    <Chip
                                                        {...getTagProps({
                                                            i,
                                                        })}
                                                        {...ownerState.ChipProps}
                                                        label={ownerState.getOptionLabel(
                                                            o,
                                                        )}
                                                        {...(o.error && {
                                                            color: 'error',
                                                            variant: 'outlined',
                                                            title: o.error
                                                                .message,
                                                        })}
                                                    />
                                                ),
                                            );
                                        }}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                variant="filled"
                                                type="tel"
                                                label={t('Phone')}
                                                error={Boolean(error)}
                                                helperText={Object.values(
                                                    error || {},
                                                )
                                                    .filter(e =>
                                                        Boolean(
                                                            e?.phone_number
                                                                ?.message,
                                                        ),
                                                    )
                                                    .map(
                                                        e =>
                                                            e?.phone_number
                                                                ?.message,
                                                    )
                                                    .join(', ')}
                                            />
                                        )}
                                    />
                                )}
                            />
                            <Controller
                                name="email_addresses"
                                control={control}
                                render={({
                                    field: { onChange, ...rest },
                                    fieldState: { error },
                                }) => (
                                    <Autocomplete
                                        multiple
                                        fullWidth
                                        freeSolo
                                        autoSelect
                                        options={[]}
                                        {...rest}
                                        onChange={(_, newValue) =>
                                            onChange(
                                                wrapLastValue(
                                                    newValue,
                                                    'email',
                                                ),
                                            )
                                        }
                                        getOptionLabel={option => option.email}
                                        ChipProps={{ color: 'primary' }}
                                        renderTags={(
                                            value,
                                            getTagProps,
                                            ownerState,
                                        ) => {
                                            const valuesWithErrors = value.map(
                                                (val, index) => ({
                                                    ...val,
                                                    error: error?.[index]
                                                        ?.email,
                                                }),
                                            );
                                            return valuesWithErrors.map(
                                                (o, i) => (
                                                    <Chip
                                                        {...getTagProps({
                                                            i,
                                                        })}
                                                        {...ownerState.ChipProps}
                                                        label={ownerState.getOptionLabel(
                                                            o,
                                                        )}
                                                        {...(o.error && {
                                                            color: 'error',
                                                            variant: 'outlined',
                                                            title: o.error
                                                                .message,
                                                        })}
                                                    />
                                                ),
                                            );
                                        }}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                variant="filled"
                                                label={t('Email')}
                                                type="email"
                                                error={Boolean(error)}
                                                helperText={Object.values(
                                                    error || {},
                                                )
                                                    .filter(e =>
                                                        Boolean(
                                                            e?.email?.message,
                                                        ),
                                                    )
                                                    .map(e => e?.email?.message)
                                                    .join(', ')}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Stack>
                    </CardContent>
                </Card>
                {/* Address */}
                <Card>
                    <CardHeader title={t('Address')} />
                    <CardContent>
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={2}>
                                <FieldSearchAutocomplete
                                    fullWidth
                                    control={control}
                                    label={t('Street')}
                                    name="street"
                                    additinalParams={streetParams}
                                />
                                <TextField
                                    variant="filled"
                                    size="small"
                                    label={t('House')}
                                    sx={{ minWidth: 80 }}
                                    {...register('house_number')}
                                />
                                <TextField
                                    variant="filled"
                                    size="small"
                                    label={t('Apt')}
                                    sx={{ minWidth: 80 }}
                                    {...register('apartment_number')}
                                />
                                <FieldSearchAutocomplete
                                    fullWidth
                                    control={control}
                                    label={t('Neighborhood')}
                                    name="neighborhood"
                                    additinalParams={neighborhoodParams}
                                />
                            </Stack>
                            <Stack direction="row" spacing={2}>
                                <FieldSearchAutocomplete
                                    control={control}
                                    label={t('City')}
                                    name="city"
                                    additinalParams={cityParams}
                                />
                                <FieldSearchAutocomplete
                                    control={control}
                                    label={t('State')}
                                    name="state"
                                    additinalParams={stateParams}
                                />
                                <Controller
                                    name="country"
                                    control={control}
                                    render={({
                                        field: { value, onChange, ...rest },
                                    }) => (
                                        <Autocomplete
                                            fullWidth
                                            autoHighlight
                                            size="small"
                                            options={countriesOptions}
                                            {...rest}
                                            onChange={(event, newValue) =>
                                                onChange(
                                                    newValue?.value || null,
                                                )
                                            }
                                            value={countriesOptions.find(
                                                option =>
                                                    option.value === value,
                                            )}
                                            renderInput={params => (
                                                <TextField
                                                    {...params}
                                                    variant="filled"
                                                    label={t('Country')}
                                                />
                                            )}
                                        />
                                    )}
                                />
                            </Stack>
                            <Stack direction="row" spacing={2}>
                                <TextField
                                    variant="filled"
                                    size="small"
                                    label={t('Zip Code')}
                                    {...register('zip_code')}
                                />
                                <TextField
                                    fullWidth
                                    variant="filled"
                                    size="small"
                                    label={t('Address Notes')}
                                    {...register('address_notes')}
                                />
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>
                {/* More Details */}
                <Card>
                    <CardHeader title={t('More Details')} />
                    <CardContent>
                        <TextField
                            fullWidth
                            variant="filled"
                            size="small"
                            multiline
                            minRows={2}
                            maxRows={4}
                            {...register('description')}
                            label={t('Description')}
                        />
                    </CardContent>
                </Card>
            </Stack>
            <RhfRtlProvider>
                <DevTool control={control} />
            </RhfRtlProvider>
        </>
    );
};

export default EditContact;
